import { useState, useEffect, useMemo } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ApproachReference } from 'types/approach';
import { getApproach } from '../api/marketplaceApi';
import { getLocalizedText } from '../utils/getLocalizedText';

export interface ResolvedApproach {
  approachSystemName: string;
  displayName: string;
  description: string | null;
}

export interface UseResolvedApproachesResult {
  approaches: ResolvedApproach[];
  loading: boolean;
}

function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolves approach system names to display metadata (name + description)
 * by fetching each approach via the schema-service API.
 */
export function useResolvedApproaches(
  subApproaches: ApproachReference[] | undefined | null
): UseResolvedApproachesResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const hasApproaches = !!subApproaches && subApproaches.length > 0;

  // Stabilize dependency: serialize system names so array reference changes don't retrigger
  const approachesKey = useMemo(
    () =>
      subApproaches
        ?.map((sa) => sa.approachSystemName)
        .sort()
        .join(',') ?? '',
    [subApproaches]
  );

  const [approaches, setApproaches] = useState<ResolvedApproach[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasApproaches) {
      setApproaches([]);
      setLoading(false);
      return;
    }

    if (!apiClients?.schemaClient || !organizationId) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);

      // Deduplicate by approachSystemName
      const uniqueNames = [...new Set((subApproaches ?? []).map((sa) => sa.approachSystemName))];

      const results: ResolvedApproach[] = [];

      for (const systemName of uniqueNames) {
        try {
          const response = await getApproach(
            apiClients.schemaClient,
            systemName,
            { includeMetrics: false }
          );

          const approach = response?.approach;

          if (approach?.name) {
            const displayName = getLocalizedText(approach.name) || formatSystemName(systemName);
            const description = approach.description
              ? getLocalizedText(approach.description) || null
              : null;

            results.push({
              approachSystemName: systemName,
              displayName,
              description,
            });
          } else {
            results.push({
              approachSystemName: systemName,
              displayName: formatSystemName(systemName),
              description: null,
            });
          }
        } catch {
          results.push({
            approachSystemName: systemName,
            displayName: formatSystemName(systemName),
            description: null,
          });
        }
      }

      if (!cancelled) {
        setApproaches(results);
        setLoading(false);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [approachesKey, hasApproaches, apiClients, organizationId]);

  return { approaches, loading };
}
